import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  CircularProgress,
  alpha,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import proxy from '../api/axios';
import { resolveProfileImage } from '../common/utils';

const ACCENT = '#137274';

const PODIUM = [
  { rank: 1, color: '#EAB308', label: '1st', height: 88 },
  { rank: 2, color: '#94A3B8', label: '2nd', height: 68 },
  { rank: 3, color: '#D97706', label: '3rd', height: 56 },
];

const NeetTestLeaderboard = () => {
  const { testId } = useParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark ? '#0f1419' : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 45%, #ffffff 100%)';

  const [test, setTest] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await proxy.get(`/neet-tests/${testId}/leaderboard`);
        if (cancelled) return;
        setTest(data.test || null);
        setLeaderboard(Array.isArray(data.leaderboard) ? data.leaderboard : []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Could not load the leaderboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLeaderboard();
    return () => {
      cancelled = true;
    };
  }, [testId]);

  const top3 = leaderboard.slice(0, 3);
  // Podium visual order: 2nd, 1st, 3rd
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, bgcolor: pageBg }}>
        <CircularProgress size={28} sx={{ color: ACCENT }} />
        <Typography sx={{ color: muted, fontWeight: 600 }}>Loading leaderboard…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: pageBg, px: { xs: 1.5, sm: 3 }, py: 3 }}>
      <Helmet>
        <title>{test?.title ? `${test.title} · Leaderboard` : 'NEET Leaderboard'} | CrafterHack</title>
      </Helmet>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Button component={Link} to="/neet-tests" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none', fontWeight: 700, color: muted, mb: 1.5, px: 0, '&:hover': { bgcolor: 'transparent', color: ACCENT } }}>
          Back to tests
        </Button>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <LeaderboardOutlinedIcon sx={{ fontSize: '18px', color: ACCENT }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: ACCENT }}>
              Leaderboard
            </Typography>
          </Box>
          <Typography component="h1" sx={{ fontWeight: 900, letterSpacing: '-0.03em', fontSize: { xs: '1.4rem', sm: '1.7rem' }, mb: 0.5 }}>
            {test?.title || 'NEET mock test'}
          </Typography>
          <Typography sx={{ color: muted, fontSize: '0.9rem' }}>
            {leaderboard.length
              ? `${leaderboard.length} submitted attempt${leaderboard.length === 1 ? '' : 's'} · out of ${test?.totalMarks ?? '—'} marks`
              : 'Rankings appear here once test-takers submit.'}
          </Typography>
        </Box>

        {error ? (
          <Box sx={{ p: 3, borderRadius: 2.5, border: `1px solid ${border}`, bgcolor: surface, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Couldn't load the leaderboard</Typography>
            <Typography sx={{ color: muted }}>{error}</Typography>
          </Box>
        ) : leaderboard.length === 0 ? (
          <Box sx={{ p: { xs: 3, sm: 5 }, borderRadius: 2.5, border: `1px solid ${border}`, bgcolor: surface, textAlign: 'center' }}>
            <EmojiEventsOutlinedIcon sx={{ fontSize: 40, color: muted, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No submissions yet</Typography>
            <Typography sx={{ color: muted, fontSize: '0.9rem' }}>Be the first to submit this test.</Typography>
          </Box>
        ) : (
          <>
            {top3.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: { xs: 1, sm: 2 }, mb: 3, px: { xs: 0.5, sm: 2 }, pt: 1 }}>
                {podiumOrder.map((p) => {
                  const meta = PODIUM.find((x) => x.rank === p.rank) || PODIUM[2];
                  const isFirst = p.rank === 1;
                  return (
                    <Box key={p.user.id} sx={{ flex: isFirst ? '1.15 1 0' : '1 1 0', maxWidth: isFirst ? 220 : 180, textAlign: 'center' }}>
                      <Avatar
                        src={resolveProfileImage(p.user?.profileImage)}
                        alt={p.user?.name}
                        sx={{
                          width: isFirst ? 72 : 56,
                          height: isFirst ? 72 : 56,
                          mx: 'auto',
                          mb: 1,
                          border: `3px solid ${meta.color}`,
                          fontWeight: 800,
                          bgcolor: alpha(meta.color, 0.2),
                          color: isDark ? '#fff' : '#0f2a2b',
                        }}
                      >
                        {(p.user?.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontWeight: 800, fontSize: isFirst ? '0.95rem' : '0.85rem', mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', px: 0.5 }}>
                        {p.user?.name || 'Test-taker'}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: muted, mb: 1 }}>
                        {p.score} / {p.maxScore}
                      </Typography>
                      <Box
                        sx={{
                          height: meta.height,
                          borderRadius: '12px 12px 4px 4px',
                          background: `linear-gradient(180deg, ${alpha(meta.color, isDark ? 0.45 : 0.55)} 0%, ${alpha(meta.color, isDark ? 0.18 : 0.22)} 100%)`,
                          border: `1px solid ${alpha(meta.color, 0.5)}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.25,
                        }}
                      >
                        <EmojiEventsOutlinedIcon sx={{ color: meta.color, fontSize: isFirst ? 28 : 22 }} />
                        <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: meta.color }}>{meta.label}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box sx={{ borderRadius: 2.5, border: `1px solid ${border}`, bgcolor: surface, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontWeight: 800 }}>Full standings</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: muted, fontWeight: 600 }}>{leaderboard.length} ranked</Typography>
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 480 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          fontWeight: 800,
                          fontSize: '0.72rem',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: muted,
                          borderBottom: `1px solid ${border}`,
                          bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.04),
                        },
                      }}
                    >
                      <TableCell width={64}>Rank</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Score</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        Correct
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        Incorrect
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaderboard.map((p) => {
                      const podium = PODIUM.find((x) => x.rank === p.rank);
                      return (
                        <TableRow key={p.user.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell sx={{ fontWeight: 800, color: podium ? podium.color : 'inherit' }}>
                            {podium ? <EmojiEventsOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} /> : null}
                            {p.rank}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar src={resolveProfileImage(p.user?.profileImage)} sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: alpha(ACCENT, 0.18), color: ACCENT }}>
                                {(p.user?.name || '?').charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{p.user?.name || 'Test-taker'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>
                            {p.score} / {p.maxScore}
                          </TableCell>
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, color: '#1a7f37', fontWeight: 700 }}>
                            {p.correctCount}
                          </TableCell>
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, color: '#cf222e', fontWeight: 700 }}>
                            {p.incorrectCount}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default NeetTestLeaderboard;
